import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { AnimatePresence, motion, MotionConfig } from 'framer-motion';
import { store } from './store/store';
import { Toaster } from './components/ui/sonner';
import { Navbar } from './components/Navbar';
import { ComparisonBar } from './components/ComparisonBar';
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
import AppInitializer from './components/AppInitializer';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PartnerSignupLayout } from './layouts/PartnerSignupLayout';
import AdminLayout from './layouts/AdminLayout';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { PartnerSignupProvider } from './contexts/PartnerSignupContext';
import { PartnerSignupFormProvider } from './contexts/PartnerSignupFormContext';
import { PropertyProvider } from './contexts/PropertyContext';
import { Footer } from './components/Footer';
import './i18n/i18n';
import './styles/global.scss';
import './App.css';

// Expose Redux store to window for crash reporting
window.__REDUX_STORE__ = store;

// ── Lazy-load all page components for route-based code splitting ──────────
const Home               = lazy(() => import('./pages/Home').then(m => ({ default: m.Home })));
const Search             = lazy(() => import('./pages/Search').then(m => ({ default: m.Search })));
const Security           = lazy(() => import('./pages/Security').then(m => ({ default: m.Security })));
const ListingDetails     = lazy(() => import('./pages/ListingDetails').then(m => ({ default: m.ListingDetails })));
const EnquiryForm        = lazy(() => import('./pages/EnquiryForm').then(m => ({ default: m.EnquiryForm })));
const EnquiryConfirmation = lazy(() => import('./pages/EnquiryConfirmation').then(m => ({ default: m.EnquiryConfirmation })));
const ExtensionRequest = lazy(() => import('./pages/ExtensionRequest').then(m => ({ default: m.ExtensionRequest })));
const ExtensionQuoteResponse = lazy(() => import('./pages/ExtensionQuoteResponse').then(m => ({ default: m.ExtensionQuoteResponse })));
const CompareProperties  = lazy(() => import('./pages/CompareProperties').then(m => ({ default: m.CompareProperties })));
const CompareQuotes      = lazy(() => import('./pages/CompareQuotes').then(m => ({ default: m.CompareQuotes })));
const SavedListings      = lazy(() => import('./pages/SavedListings').then(m => ({ default: m.SavedListings })));
const GuestProfile       = lazy(() => import('./pages/GuestProfile').then(m => ({ default: m.GuestProfile })));
const GuestNotifications = lazy(() => import('./pages/GuestNotifications').then(m => ({ default: m.GuestNotifications })));
const GuestBookingsEnquiries = lazy(() => import('./pages/GuestBookingsEnquiries').then(m => ({ default: m.GuestBookingsEnquiries })));
const GuestSupport       = lazy(() => import('./pages/GuestSupport').then(m => ({ default: m.GuestSupport })));
const Contact            = lazy(() => import('./pages/Contact').then(m => ({ default: m.Contact })));
const PartnerLanding     = lazy(() => import('./pages/PartnerLanding').then(m => ({ default: m.PartnerLanding })));
const Login              = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Register           = lazy(() => import('./pages/Register').then(m => ({ default: m.Register })));
const ForgotPassword     = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const PartnerForgotPassword = lazy(() => import('./pages/PartnerForgotPassword').then(m => ({ default: m.PartnerForgotPassword })));
const UserDashboard      = lazy(() => import('./pages/UserDashboard').then(m => ({ default: m.UserDashboard })));
const AdminLogin              = lazy(() => import('./pages/AdminLogin').then(m => ({ default: m.AdminLogin })));
// ── New Admin section pages (V2 layout) ──────────────────────────────────────
const AdminDashboardPage      = lazy(() => import('./pages/Admin/Dashboard'));
const AdminEnquiriesPage      = lazy(() => import('./pages/Admin/Enquiries'));
const AdminBookingsPage       = lazy(() => import('./pages/Admin/Bookings'));
const AdminStaysPage          = lazy(() => import('./pages/Admin/Stays'));
const AdminExtensionsPage     = lazy(() => import('./pages/Admin/Extensions'));
const AdminSupportPage        = lazy(() => import('./pages/Admin/Support'));
const AdminGuestsPage         = lazy(() => import('./pages/Admin/Guests'));
const AdminPartnersPage       = lazy(() => import('./pages/Admin/Partners'));
// const AdminChatsPage = lazy(() => import('./pages/Admin/Chats')); // Chat disabled
const AdminListingsPage       = lazy(() => import('./pages/Admin/Listings'));
const AdminReviewsPage        = lazy(() => import('./pages/Admin/Reviews'));
const AdminNotificationsPage  = lazy(() => import('./pages/Admin/Notifications'));
const AdminFinancePage        = lazy(() => import('./pages/Admin/Finance'));
const AdminAnalyticsPage      = lazy(() => import('./pages/Admin/Analytics'));
const AdminSettingsPage       = lazy(() => import('./pages/Admin/Settings'));
const AdminInvitationsPage    = lazy(() => import('./pages/Admin/Invitations'));
const AdminInviteSetup        = lazy(() => import('./pages/AdminInviteSetup').then(m => ({ default: m.AdminInviteSetup })));
// ── Legacy pages (kept during V1→V2 migration, still reachable) ──────────────
const AdminCrashReports       = lazy(() => import('./pages/AdminCrashReports').then(m => ({ default: m.AdminCrashReports })));
const AdminInvoices           = lazy(() => import('./pages/AdminInvoices').then(m => ({ default: m.AdminInvoices })));
const AdminPlatformPolicy     = lazy(() => import('./pages/AdminPlatformPolicy').then(m => ({ default: m.AdminPlatformPolicy })));
const AdminNotificationSettings = lazy(() => import('./pages/Admin/NotificationSettings'));
const PartnerLogin       = lazy(() => import('./pages/PartnerLogin').then(m => ({ default: m.PartnerLogin })));
const PartnerEmailVerification = lazy(() => import('./pages/PartnerSignup/PartnerEmailVerification').then(m => ({ default: m.PartnerEmailVerification })));
const PartnerSignupStep1 = lazy(() => import('./pages/PartnerSignup/Step1').then(m => ({ default: m.PartnerSignupStep1 })));
const PartnerSignupStep2 = lazy(() => import('./pages/PartnerSignup/Step2').then(m => ({ default: m.PartnerSignupStep2 })));
const PartnerSignupStep3 = lazy(() => import('./pages/PartnerSignup/Step3').then(m => ({ default: m.PartnerSignupStep3 })));
const PartnerSignupStep4 = lazy(() => import('./pages/PartnerSignup/Step4').then(m => ({ default: m.PartnerSignupStep4 })));
const PropertySetup      = lazy(() => import('./pages/PropertySetup').then(m => ({ default: m.PropertySetup })));
const PartnerProfile     = lazy(() => import('./pages/PartnerProfile').then(m => ({ default: m.PartnerProfile })));
const PartnerEnquiries   = lazy(() => import('./pages/PartnerEnquiries').then(m => ({ default: m.PartnerEnquiries })));
const RoomUpload         = lazy(() => import('./pages/RoomUpload').then(m => ({ default: m.RoomUpload })));
const PartnerDashboardNew = lazy(() => import('./pages/PartnerDashboardNew'));
const PartnerOnboarding = lazy(() => import('./pages/PartnerOnboarding'));
const WalletRewards      = lazy(() => import('./pages/WalletRewards'));
const PropertyManagement = lazy(() => import('./pages/Partner/PropertyManagement').then(m => ({ default: m.PropertyManagement })));
const PartnerPricingManagement = lazy(() => import('./pages/Partner/PartnerPricingManagement').then(m => ({ default: m.PartnerPricingManagement })));
const PropertyForm       = lazy(() => import('./components/Property/PropertyForm').then(m => ({ default: m.PropertyForm })));
const PropertyDetails    = lazy(() => import('./components/Property/PropertyDetails').then(m => ({ default: m.PropertyDetails })));
const NotFound           = lazy(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })));

// ── Suspense fallback shown while a route chunk is loading ────────────────
const PageLoader = () => (
  <div className="page-loader">
    <div className="page-loader-spinner" />
  </div>
);

const guestRoute = element => <ProtectedRoute roles={['USER']} loginPath="/login">{element}</ProtectedRoute>;
const partnerRoute = element => <ProtectedRoute roles={['PARTNER']} loginPath="/partner/login">{element}</ProtectedRoute>;

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const pageTransition = {
  duration: 0.3,
  ease: 'easeInOut'
};

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <Home />
            </motion.div>
          }
        />
        <Route
          path="/search"
          element={
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <Search />
            </motion.div>
          }
        />
        <Route
          path="/security"
          element={<motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition}><Security /></motion.div>}
        />
        <Route
          path="/contact"
          element={<motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition}><Contact /></motion.div>}
        />
        <Route
          path="/become-a-partner"
          element={<motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition}><PartnerLanding /></motion.div>}
        />
        <Route
          path="/listing/:id"
          element={
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <ListingDetails />
            </motion.div>
          }
        />
        <Route
          path="/listing/:id/enquiry"
          element={guestRoute(<motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition}><EnquiryForm /></motion.div>)}
        />
        <Route
          path="/compare"
          element={<motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition}><CompareProperties /></motion.div>}
        />
        <Route path="/quotes/compare" element={guestRoute(<motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition}><CompareQuotes /></motion.div>)} />
        <Route
          path="/saved-listings"
          element={guestRoute(<motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition}><SavedListings /></motion.div>)}
        />
        <Route
          path="/me/profile"
          element={guestRoute(<motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition}><GuestProfile /></motion.div>)}
        />
        <Route
          path="/me/notifications"
          element={guestRoute(<motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition}><GuestNotifications /></motion.div>)}
        />
        <Route
          path="/me/support"
          element={guestRoute(<motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition}><GuestSupport /></motion.div>)}
        />
        <Route
          path="/login"
          element={
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <Login />
            </motion.div>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <ForgotPassword />
            </motion.div>
          }
        />
        <Route
          path="/register"
          element={
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <Register />
            </motion.div>
          }
        />
        <Route
          path="/me/enquiries"
          element={guestRoute(
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <GuestBookingsEnquiries />
            </motion.div>
          )}
        />
        <Route
          path="/me/enquiries/:enquiryId/confirmation"
          element={guestRoute(<motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition}><EnquiryConfirmation /></motion.div>)}
        />
        <Route path="/extension-request" element={<ExtensionRequest />} />
        <Route path="/extension-quote-response" element={<ExtensionQuoteResponse />} />
        {/* ── Admin login (no sidebar) ─────────────────────────────────── */}
        <Route
          path="/admin/login"
          element={
            <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition}>
              <AdminLogin />
            </motion.div>
          }
        />
        <Route path="/admin/invite" element={<AdminInviteSetup />} />

        {/* ── Admin portal (with sidebar layout) ───────────────────────── */}
        <Route path="/admin/*" element={<AdminLayout />}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard"     element={<Suspense fallback={<PageLoader />}><AdminDashboardPage /></Suspense>} />
          <Route path="enquiries"     element={<Suspense fallback={<PageLoader />}><AdminEnquiriesPage /></Suspense>} />
          <Route path="bookings"      element={<Suspense fallback={<PageLoader />}><AdminBookingsPage /></Suspense>} />
          <Route path="stays"         element={<Suspense fallback={<PageLoader />}><AdminStaysPage /></Suspense>} />
          <Route path="extensions"    element={<Suspense fallback={<PageLoader />}><AdminExtensionsPage /></Suspense>} />
          <Route path="support"       element={<Suspense fallback={<PageLoader />}><AdminSupportPage /></Suspense>} />
          <Route path="support/:caseId" element={<Suspense fallback={<PageLoader />}><AdminSupportPage /></Suspense>} />
          <Route path="guests"        element={<Suspense fallback={<PageLoader />}><AdminGuestsPage /></Suspense>} />
          <Route path="partners"      element={<Suspense fallback={<PageLoader />}><AdminPartnersPage /></Suspense>} />
          {/* <Route path="chats" element={...} /> — Chat disabled */}
          <Route path="listings"      element={<Suspense fallback={<PageLoader />}><AdminListingsPage /></Suspense>} />
          <Route path="reviews"       element={<Suspense fallback={<PageLoader />}><AdminReviewsPage /></Suspense>} />
          <Route path="notifications" element={<Suspense fallback={<PageLoader />}><AdminNotificationsPage /></Suspense>} />
          <Route path="finance"       element={<Suspense fallback={<PageLoader />}><AdminFinancePage /></Suspense>} />
          <Route path="analytics"     element={<Suspense fallback={<PageLoader />}><AdminAnalyticsPage /></Suspense>} />
          <Route path="settings"      element={<Suspense fallback={<PageLoader />}><AdminSettingsPage /></Suspense>} />
          <Route path="invitations"   element={<Suspense fallback={<PageLoader />}><AdminInvitationsPage /></Suspense>} />
          {/* Legacy routes kept during migration */}
          <Route path="crash-reports" element={<Suspense fallback={<PageLoader />}><AdminCrashReports /></Suspense>} />
          <Route path="invoices"      element={<Suspense fallback={<PageLoader />}><AdminInvoices /></Suspense>} />
          <Route path="platform-policy" element={<Suspense fallback={<PageLoader />}><AdminPlatformPolicy /></Suspense>} />
          <Route path="notification-settings" element={<Suspense fallback={<PageLoader />}><AdminNotificationSettings /></Suspense>} />
        </Route>
        <Route
          path="/partner/login"
          element={
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <PartnerLogin />
            </motion.div>
          }
        />
        <Route
          path="/partner/forgot-password"
          element={
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <PartnerForgotPassword />
            </motion.div>
          }
        />
        <Route
          path="/partner/signup"
          element={
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <PartnerEmailVerification />
            </motion.div>
          }
        />
        <Route path="/partner/signup/details" element={<PartnerSignupLayout />}>
          <Route
            index
            element={<Navigate to="/partner/signup/details/step1" replace />}
          />
          <Route
            path="step1"
            element={
              <motion.div
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={pageTransition}
              >
                <PartnerSignupStep1 />
              </motion.div>
            }
          />
          <Route
            path="step2"
            element={
              <motion.div
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={pageTransition}
              >
                <PartnerSignupStep2 />
              </motion.div>
            }
          />
          <Route
            path="step3"
            element={
              <motion.div
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={pageTransition}
              >
                <PartnerSignupStep3 />
              </motion.div>
            }
          />
          <Route
            path="step4"
            element={
              <motion.div
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={pageTransition}
              >
                <PartnerSignupStep4 />
              </motion.div>
            }
          />
        </Route>
        <Route path="/partner/listings" element={partnerRoute(<Navigate to="/partner/properties" replace />)} />
        <Route
          path="/partner/property-setup"
          element={partnerRoute(
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <PropertySetup />
            </motion.div>
          )}
        />
        <Route
          path="/partner/profile"
          element={partnerRoute(
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <PartnerProfile />
            </motion.div>
          )}
        />
        <Route
          path="/partner/enquiries"
          element={partnerRoute(
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <PartnerEnquiries />
            </motion.div>
          )}
        />
        <Route
          path="/partner/dashboard"
          element={partnerRoute(
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <PartnerDashboardNew />
            </motion.div>
          )}
        />
        <Route path="/partner/onboarding" element={partnerRoute(<Suspense fallback={<PageLoader />}><PartnerOnboarding /></Suspense>)} />

        <Route
          path="/wallet"
          element={guestRoute(
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <WalletRewards />
            </motion.div>
          )}
        />
        <Route
          path="/partner/properties"
          element={partnerRoute(
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <PropertyManagement />
            </motion.div>
          )}
        />
        <Route path="/partner/pricing" element={partnerRoute(<PartnerPricingManagement />)} />
        {/* Property routes - ordered from most specific to least specific */}
        <Route
          path="/partner/properties/new"
          element={partnerRoute(
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <PropertyForm />
            </motion.div>
          )}
        />
        <Route
          path="/partner/properties/:propertyId/rooms/:roomId/edit"
          element={partnerRoute(
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <RoomUpload />
            </motion.div>
          )}
        />
        <Route
          path="/partner/properties/:propertyId/rooms/new"
          element={partnerRoute(
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <RoomUpload />
            </motion.div>
          )}
        />
        <Route
          path="/partner/properties/:propertyId/edit"
          element={partnerRoute(
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <PropertyForm />
            </motion.div>
          )}
        />
        <Route
          path="/partner/properties/:propertyId"
          element={partnerRoute(
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <PropertyDetails />
            </motion.div>
          )}
        />
        <Route
          path="*"
          element={
            <motion.div
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
            >
              <NotFound />
            </motion.div>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

// Hides the public Navbar on all /admin/* and /partner/* routes (they have their own layouts)
function AppShell() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isPartnerRoute = location.pathname.startsWith('/partner');
  const isPartnerLanding = location.pathname === '/become-a-partner';
  const isExtensionRequest = ['/extension-request', '/extension-quote-response'].includes(location.pathname);
  const impersonation = useSelector(s => s.auth.impersonation);
  return (
    <div className="App" style={impersonation ? { paddingTop: 46 } : undefined}>
      {/* Impersonation amber banner — always on top when active */}
      <ImpersonationBanner />
      {/* WCAG 2.4.1 Bypass Blocks: let keyboard/screen-reader users skip repeated nav */}
      <a href="#main-content" className="skip-to-content">Skip to main content</a>
      {!isAdminRoute && !isPartnerRoute && !isExtensionRequest && <Navbar />}
      <Suspense fallback={<PageLoader />}>
        <main id="main-content">
          <AnimatedRoutes />
        </main>
      </Suspense>
      {!isAdminRoute && !isPartnerRoute && !isExtensionRequest && <ComparisonBar />}
      {!isAdminRoute && !isPartnerRoute && !isPartnerLanding && !isExtensionRequest && <Footer />}
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      {/* Respects OS-level "reduce motion" setting for all framer-motion animations */}
      <MotionConfig reducedMotion="user">
        <AppInitializer>
          <GlobalErrorBoundary>
            <PartnerSignupFormProvider>
              <PartnerSignupProvider>
                <PropertyProvider>
                  <BrowserRouter>
                    <AppShell />
                  </BrowserRouter>
                </PropertyProvider>
              </PartnerSignupProvider>
            </PartnerSignupFormProvider>
          </GlobalErrorBoundary>
        </AppInitializer>
      </MotionConfig>
    </Provider>
  );
}

export default App;
